/**
 * Product transactions selector (SR3.4, plan §WR3 — DD!159–168, all 0708-J Must).
 *
 * The 交易紀錄 Related-Records grid as a **Tier-2 aggregate over the documents
 * the ERP owns** — sales orders, purchase orders, goods-receipt dockets and
 * customer invoices — NOT NetSuite's transaction ledger. One record, many
 * lenses: every row is projected from a source store on each read; nothing is
 * copied or stored.
 *
 * Same pure contract as the OCC / sales-history selectors:
 * - No I/O, no globals, no `Date.now()` — the caller supplies the source rows,
 *   so the UI and any future endpoint run identical logic and can never drift.
 * - Derived-never-stored: rows are recomputed from the four source arrays every
 *   call; there is no stored transaction table.
 * - FROZEN snapshots only — unit price is the line's persisted transacted price
 *   (SO `unitPrice`, PO `unitCost`, 進貨 docket line `price`, invoice line
 *   `unitPrice`), never re-resolved at read.
 *
 * 進貨 reads the S5.1 DOCKET contract (`@/types/api/goodsReceipt`) — the shape
 * `GET /goods-receipts` actually serves (api-spec.yaml `listGoodsReceipts`;
 * `goodsReceiptHandlers` are registered BEFORE `shippingHandlers`, so the
 * docket model shadows the retired codex-era route). The docket is
 * SELF-SUFFICIENT paper: `docketNumber` 貨單編號, `receiptDate`, `vendorName`,
 * `currency` and per-line `qty` / `price` / `amount` all live on it, so no PO
 * join is needed or wanted. The retired `./shipping.ts` `GoodsReceipt`
 * (`grNumber` / `receivedAt` / `line.qtyReceived`) is NOT this contract —
 * reading it blanked 單號, 數量 and 金額 on every 進貨 row.
 *
 * Columns (DD!162–168): Date · Type · Document # · Name (交易對象) · Quantity ·
 * Rate (單價) · Amount.
 *
 * AMOUNT PRECEDENCE (DD!168) — the grid must never disagree with its source
 * document, so an authoritative persisted amount always outranks arithmetic:
 *   1. the document's own frozen line amount, when it carries one and it is
 *      finite — 進貨 `line.amount`, SO `line.lineTotal`, invoice `line.amount`;
 *   2. else the document's own post-price modifier — SO `discountPct`
 *      (`qty × rate × (1 − discountPct/100)`, the SO line's own formula);
 *   3. else `roundHalfUp2(qty × rate)`.
 * Rate stays the per-unit transacted price in every case, so on a discounted
 * SO row Amount is deliberately NOT qty × Rate — that difference IS the
 * discount, and hiding it would misreport realized revenue (the same
 * lineTotal-is-revenue rule the sales-history selector already applies).
 */

import type { SalesOrder, SalesOrderLine } from '@/types/api/salesOrder';
import type { PurchaseOrder } from '@/types/api/purchaseOrder';
import type { GoodsReceipt } from '@/types/api/goodsReceipt';
import type { CustomerInvoice } from '@/types/api/finance';
import { roundHalfUp2 } from '../pricing';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The four ERP-owned document types surfaced on the grid (DD!163). */
export type ProductTransactionType = 'so' | 'po' | 'goodsReceipt' | 'invoice';

/**
 * Direction group backing the 檢視 View filter — customer-facing documents
 * (SO + invoice) vs vendor-facing documents (PO + goods receipt).
 */
export type ProductTransactionDirection = 'sales' | 'purchases';

/** One projected transaction row (one source-document line for this product). */
export interface ProductTransactionRow {
  /** Stable, source-derived key: `<type>-<docId>-<lineRef>`. */
  key: string;
  type: ProductTransactionType;
  direction: ProductTransactionDirection;
  /** Source document id — the drill target. */
  docId: string;
  /** Human document number (SO/PO/GR number or invoice number). */
  docNumber: string;
  /** 交易對象 — customer for sales rows, vendor for purchase rows; null if unknown. */
  counterpartyName: string | null;
  /** Transaction date (ISO); `''` when the source document is undated. */
  date: string;
  qty: number;
  /** 單價 (Rate) — frozen per-unit price; `null` when the source carries none. */
  unitAmount: number | null;
  /** 金額 — see the AMOUNT PRECEDENCE ladder in the module docblock. */
  amount: number | null;
  currency: string;
}

/** The four source stores the grid aggregates over. */
export interface ProductTransactionSources {
  salesOrders: readonly SalesOrder[];
  purchaseOrders: readonly PurchaseOrder[];
  goodsReceipts: readonly GoodsReceipt[];
  customerInvoices: readonly CustomerInvoice[];
}

/** 檢視 View filter value — `'all'` or one direction group. */
export type ProductTransactionView = 'all' | ProductTransactionDirection;

/** Composable filter state (View single-select + Type multi-select). */
export interface ProductTransactionFilter {
  view: ProductTransactionView;
  /** Selected document types; an empty array means "all types". */
  types: readonly ProductTransactionType[];
}

const DIRECTION_OF: Record<ProductTransactionType, ProductTransactionDirection> = {
  so: 'sales',
  invoice: 'sales',
  po: 'purchases',
  goodsReceipt: 'purchases',
};

// ---------------------------------------------------------------------------
// Amount helpers — see the AMOUNT PRECEDENCE ladder in the module docblock
// ---------------------------------------------------------------------------

/** A finite number, or `null` — the guard every precedence rung goes through. */
function finiteOrNull(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Rung 3 — `roundHalfUp2(qty × rate)`, or `null` when either side is unknown. */
function lineAmount(qty: number, unitAmount: number | null): number | null {
  if (unitAmount === null || !Number.isFinite(unitAmount) || !Number.isFinite(qty)) {
    return null;
  }
  return roundHalfUp2(qty * unitAmount);
}

/**
 * Rung 1 then 3 — an authoritative persisted amount when the document carries
 * one, else the arithmetic. Used by the document types whose line has no
 * post-price modifier of its own (進貨 docket, customer invoice).
 */
function persistedOrComputedAmount(
  persisted: number | null | undefined,
  qty: number,
  unitAmount: number | null
): number | null {
  return finiteOrNull(persisted) ?? lineAmount(qty, unitAmount);
}

/**
 * The SO line's full ladder: frozen `lineTotal` → `discountPct` → qty × rate.
 * `lineTotal` is the SO's own realized-revenue figure (the seed and the create
 * handler both derive it via `computeLineTotal`), so it outranks any recompute
 * here; `discountPct` is the fallback for a line persisted before lineTotal
 * existed. Ignoring both silently over-reported every discounted SO row.
 */
function salesOrderLineAmount(line: SalesOrderLine): number | null {
  const persisted = finiteOrNull(line.lineTotal);
  if (persisted !== null) return persisted;

  const rate = finiteOrNull(line.unitPrice);
  const discountPct = finiteOrNull(line.discountPct);
  if (rate !== null && discountPct !== null && Number.isFinite(line.qty)) {
    return roundHalfUp2(line.qty * rate * (1 - discountPct / 100));
  }
  return lineAmount(line.qty, rate);
}

// ---------------------------------------------------------------------------
// Main selector
// ---------------------------------------------------------------------------

/**
 * Project every ERP-owned transaction line referencing `productId` into a
 * unified, newest-first row list.
 *
 * @param productId Internal Product ID to scope to.
 * @param sources   The four source stores (real domain arrays satisfy these).
 * @returns rows sorted by `date` descending (undated rows last).
 */
export function buildProductTransactions(
  productId: string,
  sources: ProductTransactionSources
): ProductTransactionRow[] {
  const rows: ProductTransactionRow[] = [];
  if (!productId) return rows;

  const { salesOrders, purchaseOrders, goodsReceipts, customerInvoices } = sources;

  // Sales orders (customer-facing). Amount honours the line's own frozen
  // lineTotal / discountPct — Rate stays the undiscounted transacted price.
  for (const so of salesOrders) {
    for (const line of so.lines) {
      if (line.productId !== productId) continue;
      rows.push({
        key: `so-${so.id}-${line.id}`,
        type: 'so',
        direction: 'sales',
        docId: so.id,
        docNumber: so.soNumber,
        counterpartyName: so.customerName,
        date: so.orderDate,
        qty: line.qty,
        unitAmount: line.unitPrice,
        amount: salesOrderLineAmount(line),
        currency: line.currency,
      });
    }
  }

  // Purchase orders (vendor-facing).
  for (const po of purchaseOrders) {
    for (const line of po.lines) {
      if (line.productId !== productId) continue;
      rows.push({
        key: `po-${po.id}-${line.id}`,
        type: 'po',
        direction: 'purchases',
        docId: po.id,
        docNumber: po.poNumber,
        counterpartyName: po.vendorName,
        date: po.orderDate,
        qty: line.qty,
        unitAmount: line.unitCost,
        amount: lineAmount(line.qty, line.unitCost),
        currency: line.currency,
      });
    }
  }

  // 進貨 goods-receipt dockets — self-sufficient vendor paper (S5.1). Every
  // column reads the docket itself: 貨單編號, 收貨日, vendor, currency and the
  // line's own qty / price / amount. No PO join — the docket IS the source
  // document, and joining would let a stale PO contradict the paper.
  for (const docket of goodsReceipts) {
    for (const line of docket.lines) {
      if (line.productId !== productId) continue;
      const unitAmount = finiteOrNull(line.price);
      rows.push({
        key: `gr-${docket.id}-${line.id}`,
        type: 'goodsReceipt',
        direction: 'purchases',
        docId: docket.id,
        docNumber: docket.docketNumber,
        counterpartyName: docket.vendorName ?? null,
        date: docket.receiptDate,
        qty: line.qty,
        unitAmount,
        amount: persistedOrComputedAmount(line.amount, line.qty, unitAmount),
        currency: docket.currency,
      });
    }
  }

  // Customer invoices (發票) — invoice-on-loaded; the line carries its own
  // frozen price and amount. Header currency applies to every line.
  for (const invoice of customerInvoices) {
    for (const line of invoice.lines) {
      if (line.productId !== productId) continue;
      const unitAmount = finiteOrNull(line.unitPrice);
      rows.push({
        key: `invoice-${invoice.id}-${line.id}`,
        type: 'invoice',
        direction: 'sales',
        docId: invoice.id,
        docNumber: invoice.invoiceNumber,
        counterpartyName: invoice.customerName,
        date: invoice.issuedAt ?? invoice.createdAt,
        qty: line.qty,
        unitAmount,
        amount: persistedOrComputedAmount(line.amount, line.qty, unitAmount),
        currency: invoice.currency,
      });
    }
  }

  // Newest first; undated rows (`''`) sort to the bottom.
  rows.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  return rows;
}

// ---------------------------------------------------------------------------
// Filter (View × Type compose as an intersection)
// ---------------------------------------------------------------------------

/**
 * Apply the composable 檢視 View + 類型 Type filter. A row passes when it is in
 * the selected direction (or View is `'all'`) AND its type is selected (or no
 * type is selected, meaning all types). The two axes intersect, so narrowing
 * one never widens the other.
 */
export function filterProductTransactions(
  rows: readonly ProductTransactionRow[],
  { view, types }: ProductTransactionFilter
): ProductTransactionRow[] {
  const typeSet = types.length > 0 ? new Set(types) : null;
  return rows.filter(
    (row) =>
      (view === 'all' || row.direction === view) && (typeSet === null || typeSet.has(row.type))
  );
}

/** Direction a document type belongs to (exported for callers/tests). */
export function directionOf(type: ProductTransactionType): ProductTransactionDirection {
  return DIRECTION_OF[type];
}
